/*
 * THE IN-APP CONFIRM / PROMPT — one dialog family that replaces every
 * window.confirm and window.prompt in the app (R24).
 *
 * WHY. The browser's own pop-up can't be styled, leads with the bare host name
 * instead of FlightDeck, gives the irreversible choice no more weight than
 * Cancel, and in Safari flash-and-vanishes when it opens inside an Escape
 * keydown. This replaces all of them with the app's own dialog, on the same
 * .overlay + .modal the editing modals use.
 *
 * HOW IT IS USED. Imperatively, like the pop-ups it replaces — no JSX at the
 * call site, no host state to thread through:
 *
 *     if (!(await confirmDialog({
 *       title: 'Delete this lesson?',
 *       body: "This can't be undone.",
 *       confirmLabel: 'Delete',
 *       tone: 'danger',
 *     }))) return;
 *
 *     const email = await promptDialog({
 *       title: 'Confirm your email',
 *       label: 'Email address',
 *       required: true,
 *     });
 *     if (email === null) return;            // cancelled
 *
 * confirmDialog resolves true (confirmed) or false (cancelled / Escape /
 * click-away). promptDialog resolves the entered string, or null when
 * cancelled. A single <ConfirmHost/> mounted once at the app root renders
 * whatever is queued here; requests queue so none is ever dropped.
 */
import { create } from 'zustand';
import type { ReactNode } from 'react';

/** Destructive = permanent loss (delete/withdraw/remove), weighted in critical
 *  red. Caution = abandoning an in-progress change (discard), weighted in brand
 *  petrol — red stays reserved for permanent loss, so the colour stays honest. */
export type ConfirmTone = 'danger' | 'caution';

interface Common {
  /** The question. Short — the title IS the ask. */
  title: string;
  /** The consequence. A string, or JSX when a noun should read in bold. */
  body?: ReactNode;
}

export interface ConfirmRequest extends Common {
  kind: 'confirm';
  /** The word on the action button: "Delete", "Withdraw", "Remove", "Discard". */
  confirmLabel: string;
  /** The quiet button; defaults to "Cancel" (or, for caution, call it "Keep editing"). */
  cancelLabel?: string;
  tone: ConfirmTone;
  /**
   * ACKNOWLEDGE ONLY — no quiet button, so there is no way through. For a refusal
   * the person cannot override (a hard dependency block set by an admin): the one
   * button just says OK, and dismissing by Escape or the backdrop does not let the
   * blocked thing happen either. The promise still resolves false on dismiss.
   */
  alert?: boolean;
}

export interface PromptRequest extends Common {
  kind: 'prompt';
  /** The field label above the input. */
  label: string;
  /**
   * A read-only "current value" field shown ABOVE the editable one, for a prompt
   * that corrects an existing value (e.g. "Current" address, then the new one).
   * Disabled, so it reads as context the person cannot edit — the shared confirm
   * primitive rather than a second dialog family (R24). Absent for an ordinary
   * prompt.
   */
  current?: { label: string; value: string };
  placeholder?: string;
  initial?: string;
  /** A required field shows the red asterisk and holds Submit until filled. */
  required?: boolean;
  /** The submit button word; defaults to "Confirm". */
  submitLabel?: string;
  cancelLabel?: string;
}

type Request =
  | (ConfirmRequest & { id: number; resolve: (ok: boolean) => void })
  | (PromptRequest & { id: number; resolve: (value: string | null) => void });

interface ConfirmStore {
  current: Request | null;
  queue: Request[];
  /** Internal: push a built request (the imperative helpers below call this). */
  enqueue: (r: Request) => void;
  /**
   * Resolve the awaiting caller with the chosen result, THE INSTANT the choice is
   * made — so the action behind `await confirmDialog(...)` runs now, not after the
   * dialog's ~240ms leave animation. (That deferral left a dead window where, e.g.,
   * "Start anyway" had been confirmed but the card had not yet moved, which read as
   * a dead click.) The dialog stays on screen to animate out; `closeCurrent` takes
   * it off after. The dialog latches its choice, so this is only ever called once
   * per request — a stray cancel during the leave cannot re-resolve it.
   */
  resolveCurrent: (result: boolean | string | null) => void;
  /** Take the settled dialog off screen and advance to any queued one. The dialog
   *  calls this AFTER its leave animation. */
  closeCurrent: () => void;
}

export const useConfirmStore = create<ConfirmStore>((set, get) => ({
  current: null,
  queue: [],
  enqueue: (r) => {
    if (get().current) set((s) => ({ queue: [...s.queue, r] }));
    else set({ current: r });
  },
  resolveCurrent: (result) => {
    const cur = get().current;
    if (!cur) return;
    // Each request's resolve has the right signature for its own kind; the
    // dialog only ever passes a boolean for 'confirm' and string|null for
    // 'prompt', so this cast is sound at every call.
    (cur.resolve as (r: boolean | string | null) => void)(result);
  },
  closeCurrent: () => {
    const [next, ...rest] = get().queue;
    set({ current: next ?? null, queue: rest });
  },
}));

let nextId = 1;

/** Ask the person to confirm an action. Resolves true (confirmed) or false
 *  (cancelled, Escape, or click-away). The drop-in for window.confirm. */
export function confirmDialog(opts: Omit<ConfirmRequest, 'kind'>): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    useConfirmStore.getState().enqueue({ ...opts, kind: 'confirm', id: nextId++, resolve });
  });
}

/** Ask the person for one line of text. Resolves the entered string, or null
 *  when cancelled. The drop-in for window.prompt. */
export function promptDialog(opts: Omit<PromptRequest, 'kind'>): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    useConfirmStore.getState().enqueue({ ...opts, kind: 'prompt', id: nextId++, resolve });
  });
}
